import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import AdminLoginPage from './pages/auth/AdminLoginPage';
import AdminRegisterPage from './pages/auth/AdminRegisterPage';
import StudentRegisterPage from './pages/auth/StudentRegisterPage';
import RegistrationStatusPage from './pages/auth/RegistrationStatusPage';

// Public Pages
import LandingPage from './pages/LandingPage';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import StudentsPage from './pages/admin/StudentsPage';
import QuestionsPage from './pages/admin/QuestionsPage';
import AddQuestionPage from './pages/admin/AddQuestionPage';
import TestsPage from './pages/admin/TestsPage';
import AdminResultsPage from './pages/admin/AdminResultsPage';
import LeaderboardPage from './pages/admin/LeaderboardPage';
import ExamSettingsPage from './pages/admin/ExamSettingsPage';
import CreateAdminPage from './pages/admin/CreateAdminPage';
import SuperAdminPage from './pages/admin/SuperAdminPage';

// Student Pages
import StudentDashboard from './pages/student/StudentDashboard';
import ExamPage from './pages/student/ExamPage';
import ResultPage from './pages/student/ResultPage';
import ReviewPage from './pages/student/ReviewPage';

// ─── Route Guards ─────────────────────────────────────────────────────────────

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="loader" />
  </div>
);

/** Any logged-in admin (superadmin or admin) */
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin'))
    return <Navigate to="/admin/login" replace />;
  return children;
};

/** Super admin only — used for Create Admin page */
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || user.role !== 'superadmin')
    return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const StudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user || user.role !== 'student') return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user?.role === 'superadmin' || user?.role === 'admin')
    return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'student') return <Navigate to="/student/dashboard" replace />;
  return children;
};

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/admin/login" element={<PublicRoute><AdminLoginPage /></PublicRoute>} />
      <Route path="/admin/register" element={<PublicRoute><AdminRegisterPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><AdminRegisterPage /></PublicRoute>} />
      <Route path="/student/register" element={<StudentRegisterPage />} />
      <Route path="/student/status" element={<RegistrationStatusPage />} />

      {/* Admin Routes — all tabs open to every admin */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard"     element={<AdminDashboard />} />
        <Route path="students"      element={<StudentsPage />} />
        <Route path="questions"     element={<QuestionsPage />} />
        <Route path="questions/add" element={<AddQuestionPage />} />
        <Route path="questions/edit/:id" element={<AddQuestionPage />} />
        <Route path="tests"         element={<TestsPage />} />
        <Route path="results"       element={<AdminResultsPage />} />
        <Route path="leaderboard"   element={<LeaderboardPage />} />
        <Route path="exam-settings" element={<ExamSettingsPage />} />

        {/* Create Admin — superadmin only, isolated */}
        <Route path="create-admin" element={
          <SuperAdminRoute><CreateAdminPage /></SuperAdminRoute>
        } />

        {/* Manage Admins — superadmin only */}
        <Route path="manage-admins" element={
          <SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>
        } />
      </Route>

      {/* Student Routes */}
      <Route path="/student/dashboard" element={<StudentRoute><StudentDashboard /></StudentRoute>} />
      <Route path="/student/exam/:testId" element={<StudentRoute><ExamPage /></StudentRoute>} />
      <Route path="/student/result/:testId" element={<StudentRoute><ResultPage /></StudentRoute>} />
      <Route path="/student/review/:testId" element={<StudentRoute><ReviewPage /></StudentRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { borderRadius: '10px', fontSize: '14px' }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
